import { useCallback, useEffect, useRef, useState } from 'react';
import { climbScansService } from '../../../services/climb-scans';
import type { ClimbScan } from '../../../types/climb-scan';
import { wallScanFrom, type WallScan } from './wall-scan';

/** How often the scan is read back while it runs. */
export const LIVE_SCAN_POLL_MS = 1000;

/**
 * How long the page waits before giving up on it: past the server's own
 * ceiling (RfDetrDetector::TIMEOUT, 300 s), with room for the queue.
 */
export const LIVE_SCAN_GIVE_UP_MS = 6 * 60 * 1000;

/**
 * Where a photo being read for holds has got to.
 *
 * - `uploading` — the photo is going up;
 * - `queued` — up, and waiting for the worker;
 * - `scanning` — the hold model is answering, tile by tile (`fraction`);
 * - `finishing` — every tile is in, and the holds are being worked out of them.
 */
export type LiveScanPhase = 'uploading' | 'queued' | 'scanning' | 'finishing';

export interface LiveScanState {
  phase: LiveScanPhase;
  /** Tiles answered out of the calls issued, 0–1; null before the first. */
  fraction: number | null;
  done: number | null;
  total: number | null;
}

/** How a scan ended, for the page to say. */
export type LiveScanOutcome =
  | { kind: 'found'; url: string; scan: WallScan }
  | { kind: 'empty' }
  | { kind: 'failed' };

interface LiveScanArgs {
  locationSlug: string | undefined;
  /** Called once per photo, with what came of reading it. */
  onDone: (outcome: LiveScanOutcome) => void;
  /** Sends the photo up and answers its signed id (`usePhotoUpload`). */
  upload: (file: File) => Promise<string>;
}

/**
 * **A photo read for holds while the setter waits** (#320): the camera's or the
 * gallery's picture goes up as a scan (`ClimbScans::LiveScan`), and the page
 * reads it back every second until its holds are in — then they are offered on
 * the very photo that is on screen, as on any scanned wall.
 *
 * Not a request held open: the hold model answers on Lambda in seconds, but
 * what is made of its answer — the filter, the colours, the grouping — is ours
 * and runs in the worker, so the page asks how far it has got rather than
 * holding a web process for it.
 *
 * One photo at a time. Starting another, or `cancel`, drops whatever the last
 * one was going to say: a wall's holds belong to the photo they were measured
 * on, and the page has moved on from it.
 */
export function useLiveScan({ locationSlug, onDone, upload }: LiveScanArgs) {
  const [state, setState] = useState<LiveScanState | null>(null);
  // Which run is the current one: anything a superseded run says is ignored.
  const run = useRef(0);
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  useEffect(
    () => () => {
      run.current += 1;
    },
    []
  );

  const cancel = useCallback(() => {
    run.current += 1;
    setState(null);
  }, []);

  /** Read the photo drawn from `url` for holds. */
  const start = useCallback(
    async (file: File, url: string) => {
      if (!locationSlug) return;
      const mine = ++run.current;
      const current = () => run.current === mine;
      const finish = (outcome: LiveScanOutcome) => {
        if (!current()) return;
        setState(null);
        onDoneRef.current(outcome);
      };

      setState({ phase: 'uploading', fraction: null, done: null, total: null });
      try {
        const signedId = await upload(file);
        if (!current()) return;

        let scan = await climbScansService.startLiveScan(locationSlug, {
          image_signed_id: signedId,
          climb_type: 'boulder',
        });
        const giveUpAt = Date.now() + LIVE_SCAN_GIVE_UP_MS;

        while (current()) {
          if (scan.status === 'ready') {
            const wall = wallScanFrom(scan);
            return finish(wall.detections.length > 0 ? { kind: 'found', url, scan: wall } : { kind: 'empty' });
          }
          if (scan.status === 'failed' || Date.now() > giveUpAt) return finish({ kind: 'failed' });

          setState((previous) => stateOf(scan, previous));
          await wait(LIVE_SCAN_POLL_MS);
          if (!current()) return;
          scan = await climbScansService.getScan(locationSlug, scan.id);
        }
      } catch {
        finish({ kind: 'failed' });
      }
    },
    [locationSlug, upload]
  );

  return { state, start, cancel };
}

/**
 * Where a scan read back has got to. The bar never steps back: the calls
 * issued grow when a full tile is quartered, which is the scan finding more
 * to do, not undoing what it did.
 */
function stateOf(scan: ClimbScan, previous: LiveScanState | null): LiveScanState {
  const progress = scan.progress;
  if (scan.status === 'pending' || !progress) {
    return { phase: 'queued', fraction: null, done: null, total: null };
  }
  if (progress.stage === 'holds') {
    return { phase: 'finishing', fraction: 1, done: previous?.total ?? null, total: previous?.total ?? null };
  }

  const fraction = progress.total > 0 ? progress.done / progress.total : 0;
  return {
    phase: 'scanning',
    fraction: Math.max(fraction, previous?.fraction ?? 0),
    done: progress.done,
    total: progress.total,
  };
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
